import React from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import SettingToggleCard from "./SettingToggleCard";
import DirectAssignabilityToggle from "./DirectAssignabilityToggle";

const labelClass = "block text-xs font-medium text-gray-700 mb-1.5";
const selectOutlineSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};

/**
 * Right rail: form-level settings (who-fills, scoring, profile copy, direct
 * assignability, delete). Collapsible, mirroring the quiz editor's
 * QuizSettingsPane. Presentational — all changes bubble up.
 */
export function FormSettingsPane({
  isEdit,
  // "engagement" = the Engagement tab's survey editor: only description, the
  // slide-show toggle and Delete. Everything else here is a training concept.
  variant = "training",
  description,
  onDescriptionChange,
  completedByRole,
  onCompletedByRoleChange,
  scoringEnabled,
  onScoringChange,
  passThreshold,
  onPassThresholdChange,
  gateOnPass,
  onGateChange,
  hasScoredField,
  thresholdNum,
  maxPossibleScore,
  saveToProfile,
  onSaveToProfileChange,
  directlyAssignable,
  onDirectlyAssignableChange,
  presentation = "list",
  onPresentationChange,
  onClose,
  onDeleteClick,
  deleteDisabled,
}) {
  const isEngagement = variant === "engagement";
  return (
    <div className="w-[340px] shrink-0 border-l border-slate-200 bg-white flex flex-col overflow-hidden">
      <div className="h-14 px-5 border-b border-slate-200 flex items-center justify-between shrink-0">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          {isEngagement ? "Survey Settings" : "Form Settings"}
        </p>
        <IconButton size="small" onClick={onClose} title="Hide settings">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
        {/* Description / instructions */}
        <div>
          <label className={labelClass}>Description</label>
          <textarea
            placeholder="Instructions shown above the form"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700 resize-none"
          />
        </div>

        {/* Presentation: scrolling list vs one question per screen */}
        <SettingToggleCard
          checked={presentation === "slides"}
          onChange={(v) => onPresentationChange && onPresentationChange(v ? "slides" : "list")}
          title="Slide show"
          description="Survey-style: one question per screen with a progress bar. Add an Info screen field for a welcome, a transition, or a closing thank-you. Off = a single scrolling form."
        />

        {/* Training-only settings: who fills it, scoring, profile copy,
            assignability. A survey is always self-filled via a link and never
            scored, so the Engagement variant ends here. */}
        {!isEngagement && (
          <>
        {/* Who fills it out */}
        <div>
          <label className={labelClass}>When assigned, who fills it out?</label>
          <Select
            value={completedByRole}
            onChange={(e) => onCompletedByRoleChange(e.target.value)}
            size="small"
            fullWidth
            sx={{ height: 40, fontSize: 14, borderRadius: "8px", ...selectOutlineSx }}
          >
            <MenuItem value="employee" sx={{ fontSize: 14 }}>
              The person assigned (self)
            </MenuItem>
            <MenuItem value="manager" sx={{ fontSize: 14 }}>
              Their manager
            </MenuItem>
          </Select>
          <p className="mt-1.5 text-xs text-slate-500">
            Only applies when this form is assigned as training. Collecting from
            customers or anyone else? Use the Share link instead, no login needed.
          </p>
        </div>

        {/* Scoring */}
        <SettingToggleCard
          checked={scoringEnabled}
          onChange={onScoringChange}
          title="Enable scoring"
          description="Sum points from scale / single-select fields and set a pass threshold"
        >
          {scoringEnabled && (
            <div>
              <label className={labelClass}>Pass threshold (total points)</label>
              <input
                type="number"
                placeholder="e.g., 32"
                value={passThreshold}
                onChange={(e) => onPassThresholdChange(e.target.value)}
                className="w-40 px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
              />
              <p className="text-xs text-gray-500 mt-1">
                Mark each scoring field with "Counts toward score".
              </p>

              {passThreshold !== "" && !hasScoredField && (
                <p className="text-xs text-red-500 mt-1">
                  No fields count toward the score yet. Mark at least one scale or
                  single-select field as "Counts toward score".
                </p>
              )}
              {passThreshold !== "" &&
                hasScoredField &&
                Number.isFinite(thresholdNum) &&
                thresholdNum > maxPossibleScore && (
                  <p className="text-xs text-red-500 mt-1">
                    Threshold exceeds the maximum possible score ({maxPossibleScore}).
                    Lower it or add scored fields.
                  </p>
                )}

              {passThreshold !== "" && (
                <label className="flex items-start gap-2 cursor-pointer mt-4 pt-3 border-t border-gray-200">
                  <input
                    type="checkbox"
                    checked={gateOnPass}
                    onChange={(e) => onGateChange(e.target.checked)}
                    className="accent-violet-600 w-5 h-5 shrink-0 mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      Require passing to complete the section
                    </div>
                    <div className="text-xs text-gray-500">
                      Off: the score is recorded but a failing learner can still move
                      on. On: a failing score blocks the section until they pass, like
                      a failing quiz.
                    </div>
                  </div>
                </label>
              )}
            </div>
          )}
        </SettingToggleCard>

        {/* Save to employee profile */}
        <SettingToggleCard
          checked={saveToProfile}
          onChange={onSaveToProfileChange}
          title="Keep a copy on the employee's profile"
          description="The submission stays on their profile even after the training changes or is unassigned."
        />

        {/* Make directly assignable */}
        <DirectAssignabilityToggle
          value={directlyAssignable}
          onChange={onDirectlyAssignableChange}
        />
          </>
        )}

        {isEdit && (
          <div className="pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onDeleteClick}
              disabled={deleteDisabled}
              className="w-full py-2 px-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm flex items-center justify-center gap-2 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-50"
            >
              <DeleteOutlineIcon fontSize="small" />
              {isEngagement ? "Delete Survey" : "Delete Form"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
